import type { ViewMode } from '@mobile-mapping/web3d';
import {
  FeatureToggleCapabilityCoordinator,
  PaneVisibilityCoordinator,
  ViewModeCapabilityCoordinator,
} from '@mobile-mapping/web3d';
import type { ActiveItems, ProjectDataState } from '@mobile-mapping/providers';
import { getVisibleTrajectoryIds } from '@mobile-mapping/providers';
import type { Markup } from '@data-access/connect/tc-tcps';
import { CapabilityRegistry } from '@trimble/web3d';
import type { CapabilityRegistryContainer } from '@trimble/web3d';
import type { PaneLayouts } from '@trimble/pane-layout';
import {
  AlignmentNavigationCapability,
  ClippingCapability,
} from '@trimble/web3d-capabilities';
import {
  AlignmentNavigationStatus,
  type StartNavigationOptions,
} from '@technology/web3d-plugin-trimbim';
import { StationsCapabilityProvider } from '@trimble/web3d-providers';
import type { Measurement, PositioningData } from '@trimble/web3d-providers';
import type { StationInfo } from '@trimble/web3d-providers';
import {
  MULTIPANE_RESERVED_DATA_MAX_CHARS,
  MULTIPANE_RESERVED_DATA_VERSION,
} from './multipane-view.types';
import type {
  MultipaneCellContentConfig,
  MultipaneReservedData,
  PaneNavigationState,
  SavedAlignmentIdentity,
  SavedStationIdentity,
} from './multipane-view.types';
import { toReservedMeasurements } from './reserved-measurements';
import { collectConnectMeasurementData } from './measurement-labels';

/** Convert a global station index to a run-local index. */
function computeLocalStationIndex(
  stationsInfo: readonly StationInfo[],
  globalIndex: number
): number {
  const targetRunId = stationsInfo[globalIndex].runId;
  let localIndex = 0;
  for (let i = 0; i < globalIndex; i++) {
    if (stationsInfo[i].runId === targetRunId) {
      localIndex++;
    }
  }
  return localIndex;
}

/** Find the currently selected station across all panes, if any. */
function getSelectedStationIdentity(
  capabilityRegistry: CapabilityRegistry
): SavedStationIdentity | undefined {
  const stationsProvider = capabilityRegistry.get(StationsCapabilityProvider)();
  if (!stationsProvider) return undefined;

  const info = stationsProvider.stationsInfo();
  if (!info.length) return undefined;

  for (const pane of capabilityRegistry.paneContainers()) {
    const controller = pane
      .get(ViewModeCapabilityCoordinator)()
      ?.stationNavigationController();
    if (!controller) continue;

    const globalIdx = controller.selectedStationIndex();
    if (globalIdx == null) continue;

    const station = info[globalIdx];
    if (!station?.runId) continue;

    return {
      runId: station.runId,
      localStationIndex: computeLocalStationIndex(info, globalIdx),
    };
  }

  return undefined;
}

/** Determine the navigation state of a single pane. */
async function getPaneNavigationState(
  pane: CapabilityRegistryContainer
): Promise<PaneNavigationState> {
  const alignmentCap = pane.get(AlignmentNavigationCapability)();
  if (
    alignmentCap &&
    alignmentCap.status() === AlignmentNavigationStatus.Navigating &&
    alignmentCap.alignmentModelId()
  ) {
    const fullState = await alignmentCap.getNavigationState();
    const options: StartNavigationOptions = {
      crossSectionEnabled: fullState?.crossSectionEnabled,
      section: fullState?.section,
      web3dChainage: fullState?.web3dChainage,
      direction: fullState?.direction,
    };
    return { kind: 'alignmentNavigation', options };
  }

  const controller = pane
    .get(ViewModeCapabilityCoordinator)()
    ?.stationNavigationController();
  if (controller && controller.selectedStationIndex() != null) {
    return { kind: 'station' };
  }

  return { kind: 'freeView' };
}

/** Snapshot the current multipane state (camera, clipping, view mode, etc.) for persistence. */
export async function getMultipaneReservedData(
  capabilityRegistry: CapabilityRegistry,
  layouts: PaneLayouts<ViewMode>,
  activeItems: ActiveItems,
  dataState: ProjectDataState,
  createdMarkups: readonly Markup[] = [],
  submittedMeasurements: readonly Measurement[] = []
): Promise<Record<string, unknown>> {
  const activeLayout =
    layouts.layouts.find((l) => l.label === layouts.activeLayoutLabel) ??
    layouts.layouts[0];

  if (!activeLayout) {
    throw new Error(
      'Cannot save multipane reserved data: no active layout available'
    );
  }

  const panes = capabilityRegistry.paneContainers();

  const paneRows = await Promise.all(
    activeLayout.paneRows.map(async (row, rowIdx) => {
      const cells = await Promise.all(
        row.cells.map(async (cell, cellIdx) => {
          const pane = panes.find(
            (p) => p.paneRowIdx === rowIdx && p.paneCellIdx === cellIdx
          );

          const viewer = pane?.viewerWrapper()?.viewer;

          const camera: PositioningData = viewer
            ? {
                cameraPosition: {
                  x: viewer.camera.position.x,
                  y: viewer.camera.position.y,
                  z: viewer.camera.position.z,
                },
                targetPosition: null,
                quaternion: viewer.camera.quaternion.toArray() as [
                  number,
                  number,
                  number,
                  number,
                ],
                fov: viewer.camera.fieldOfView ?? null,
              }
            : {
                cameraPosition: null,
                targetPosition: null,
                quaternion: null,
                fov: null,
              };

          const clippingCap = pane?.get(ClippingCapability)();

          const navigationState = pane
            ? await getPaneNavigationState(pane)
            : undefined;

          const paneVisibility = pane
            ?.get(PaneVisibilityCoordinator)()
            ?.getSnapshot();

          const contentConfig: MultipaneCellContentConfig = {
            camera,
            clipping: {
              sectionPlanes: clippingCap?.getSectionPlanes() ?? [],
              sectionBox: clippingCap?.getSectionBox() ?? null,
            },
            viewMode: cell.contentConfig,
            featureToggles:
              pane
                ?.get(FeatureToggleCapabilityCoordinator)()
                ?.getFeatureToggleState() ?? {},
            ...(navigationState && { navigationState }),
            ...(paneVisibility && { paneVisibility }),
          };

          return {
            size: cell.size,
            minSize: cell.minSize,
            contentConfig,
          };
        })
      );

      return {
        config: { size: row.config.size, minSize: row.config.minSize },
        cells,
      };
    })
  );

  const station = getSelectedStationIdentity(capabilityRegistry);

  const alignmentId = activeItems.alignmentId;
  const alignment: SavedAlignmentIdentity | undefined = alignmentId
    ? { alignmentId }
    : undefined;

  const loadedTrajectoryIds = getVisibleTrajectoryIds(dataState) ?? [];

  // Alignment records and Connect measurement data both come from the submitted
  // snapshot so they agree with each other and with the rest of the saved view.
  const measurements = toReservedMeasurements(submittedMeasurements);
  const connectMeasurementData = collectConnectMeasurementData(
    submittedMeasurements,
    createdMarkups
  );

  const data: MultipaneReservedData = {
    layout: { label: activeLayout.label, paneRows },
    version: MULTIPANE_RESERVED_DATA_VERSION,
    ...(station && { station }),
    ...(alignment && { alignment }),
    ...(loadedTrajectoryIds.length > 0 && { loadedTrajectoryIds }),
    ...(measurements.length > 0 && { measurements }),
    ...(connectMeasurementData.length > 0 && { connectMeasurementData }),
  };

  const recordResult = multipaneReservedDataToRecord(data);
  if (!recordResult.success) {
    throw new Error(recordResult.error);
  }
  return recordResult.record;
}

export type PayloadSizeValidationResult =
  | { readonly success: true; readonly serialized: string }
  | { readonly success: false; readonly error: string };

/** Checks that the serialized payload fits within the size limit. */
export function validatePayloadSize(
  data: MultipaneReservedData
): PayloadSizeValidationResult {
  const serialized = JSON.stringify(data);
  if (serialized.length > MULTIPANE_RESERVED_DATA_MAX_CHARS) {
    return {
      success: false,
      error: `Serialized payload is ${serialized.length} chars, exceeds ${MULTIPANE_RESERVED_DATA_MAX_CHARS} char limit`,
    };
  }
  return { success: true, serialized };
}

export type MultipaneReservedDataRecordResult =
  | { readonly success: true; readonly record: Record<string, unknown> }
  | { readonly success: false; readonly error: string };

/** JSON round-trip to a plain serializable object; validates size first. */
export function multipaneReservedDataToRecord(
  data: MultipaneReservedData
): MultipaneReservedDataRecordResult {
  const validation = validatePayloadSize(data);
  if (!validation.success) {
    return { success: false, error: validation.error };
  }
  return {
    success: true,
    record: JSON.parse(validation.serialized) as Record<string, unknown>,
  };
}
